import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, CircularProgress, IconButton, Tab, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { Helper } from "../../utils/Helpers";
import EmployeeOverview, { EmployeeProfessional } from "./EmployeeOverview";

const ViewEmployee = ({ employee, onBack }) => {
  const { t } = useTranslation();
  const { api } = ApiClient();
  const [value, setValue] = useState("1");
  const [lookups, setLookups] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    let isActive = true;
    const { convertToSelectFieldValues } = new Helper();

    const toOptions = (settled) => {
      const response = settled?.status === "fulfilled" ? settled.value : null;
      if (response?.status !== 200 || !Array.isArray(response?.data?.data)) {
        return [];
      }
      try {
        return convertToSelectFieldValues(response.data.data);
      } catch (error) {
        console.error("Unexpected lookup shape", error);
        return [];
      }
    };

    const fetchLookups = async () => {
      setIsLoading(true);
      try {
        const requests = [
          api.get(END_POINT.EMPLOYEE.GET_ALL_MARITAL_STATUS),
          api.get(END_POINT.EMPLOYEE.GET_ALL_DEPARTMENT),
          api.get(END_POINT.EMPLOYEE.GET_ALL_DESIGNATION),
          api.get(END_POINT.EMPLOYEE.GET_ALL_WORK_TYPE),
          api.get(END_POINT.EMPLOYEE.GET_ALL_EDUCATION_LEVELS),
          api.get(END_POINT.UTILS.GET_ALL_COUNTRIES),
          employee?.country_id
            ? api.get(`${END_POINT.UTILS.GET_STATE}/${employee.country_id}`)
            : Promise.resolve(null),
          employee?.state_id
            ? api.get(`${END_POINT.UTILS.GET_CITY}/${employee.state_id}`)
            : Promise.resolve(null),
        ];

        const [
          maritalStatus,
          departments,
          designations,
          workTypes,
          educationLevels,
          countries,
          states,
          cities,
        ] = await Promise.allSettled(requests);

        if (!isActive) return;

        setLookups({
          maritalStatus: toOptions(maritalStatus),
          departments: toOptions(departments),
          designations: toOptions(designations),
          workTypes: toOptions(workTypes),
          educationLevels: toOptions(educationLevels),
          countries: toOptions(countries),
          states: toOptions(states),
          cities: toOptions(cities),
        });
      } catch (error) {
        console.error("Error fetching employee lookups", error);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchLookups();
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.country_id, employee?.state_id]);

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "#fff6e8",
          borderRadius: "8px 8px 0 0",
          padding: { xs: "2% 3%", md: "1% 1.5%" },
        }}
      >
        <IconButton onClick={onBack} size="small" aria-label={t("pages.employee.view.back")}>
          <ArrowBack sx={{ color: "#DEA829" }} fontSize="small" />
        </IconButton>
        <Typography
          sx={{
            fontWeight: 600,
            color: "#474747",
            fontSize: { xs: "0.95rem", md: "1.05rem" },
          }}
        >
          {employee?.name || t("pages.employee.view.title")}
        </Typography>
      </Box>

      <TabContext value={value}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "#FFF9EC",
            p: { xs: "0 3%", md: "0 4%" },
          }}
        >
          <TabList
            onChange={handleChange}
            aria-label="employee tabs"
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: { xs: "6%", md: "6%" },
              },
              "& .MuiTab-root": {
                fontSize: { xs: "0.85rem", md: "1rem" },
                fontWeight: 500,
                minWidth: "unset",
                padding: "12px 0",
                textTransform: "none",
                "&.Mui-selected": {
                  color: "#D8A630",
                },
              },
            }}
          >
            <Tab label={t("pages.employee.view.overview")} value="1" />
            <Tab label={t("pages.employee.view.professional")} value="2" />
          </TabList>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: "6%" }}>
            <CircularProgress size={28} sx={{ color: "#DEA829" }} />
          </Box>
        ) : (
          <>
            <TabPanel value="1" sx={{ p: { xs: "4% 3%", md: "2% 4%" } }}>
              <EmployeeOverview employee={employee} lookups={lookups} />
            </TabPanel>
            <TabPanel value="2" sx={{ p: { xs: "4% 3%", md: "2% 4%" } }}>
              <EmployeeProfessional employee={employee} lookups={lookups} />
            </TabPanel>
          </>
        )}
      </TabContext>
    </Box>
  );
};

export default ViewEmployee;
